/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
	PARTITIONING_ERROR_FIREBASE_PERMISSION,
	PARTITIONING_ERROR_PROJECT_CATALOG_NOT_FOUND,
	PARTITIONING_ERROR_REPARTITION_IN_PROGRESS,
} from '@touchplan/strings';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import {
	catchError,
	filter,
	groupBy,
	map,
	mergeMap,
	pairwise,
	scan,
	startWith,
	switchMap,
	takeUntil,
	takeWhile,
	tap,
} from 'rxjs/operators';
import { ObservableJar } from 'src/app/plain-javascript/ObservableJar';
import { AppState } from 'src/app/services/app-state.service';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { environment } from 'src/environments/environment-variables';
import { FeatureFlagManager } from '../../../services/FeatureFlagManager';
import {
	IFancyFirebaseStateCatalogGetter,
	IProjectCatalogData,
	fancyFirebaseGetters,
	ProjectCatalogType,
	masterCatalog,
	FirebaseStateError,
} from '@touchplan/fancy-referencer';
import { UserIndex } from '../lists/UserIndexList';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../../logging/LoggerServiceFactory';

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseState implements OnDestroy, IFancyFirebaseStateCatalogGetter {
	private destroy$: Subject<void>;
	private logger: ILoggerService;

	private catalogJar = new ObservableJar((combinedId: string) => {
		return this.processProjectCatalog$(combinedId); //this function returns an observable
	});

	constructor(
		private angularFireDatabase: AngularFireDatabase,
		private appState: AppState,
		private featureFlagManager: FeatureFlagManager
	) {
		this.logger = LoggerServiceFactory.create('FancyFirebaseState');
		this.destroy$ = new Subject();
		this.appState.user
			.pipe(
				filter((user) => !!user),
				takeUntil(this.destroy$)
			)
			.subscribe((data) => {
				// rare case that user gets added to/removed from a project
				// need to clean up the cache if subscription to that project catalog exists
				this.cleanUpCache(data.indices);
			});
		this.startReloadDetector();
	}

	private startReloadDetector() {
		//may be some merit to making this more generic/ utility? Probably only relevant as an optional extra layer on top of ObservableJar
		this.catalogJar.uniqueNotifier
			.pipe(
				groupBy((val) => val.id),
				mergeMap((group$) => {
					return group$.pipe(
						takeWhile((ob) => ob.ob$ !== null), //to stop listening when it's "removed" because mergeMap doesn't auto-clean up
						switchMap((group) => {
							return group.ob$.pipe(
								catchError((err) => {
									//let the repartition error through
									if (err.message === PARTITIONING_ERROR_REPARTITION_IN_PROGRESS) {
										this.logger.warn('expected partition error', err);
										return of({} as IProjectCatalogData); //will always reload, none is different than something
									}
									this.logger.error(err);
									return EMPTY;
								})
							); //skip errors
						}),
						pairwise()
					);
				})
			)
			.subscribe(([prevCatalog, currentCatalog]) => {
				if (
					prevCatalog &&
					(currentCatalog.databaseURL !== prevCatalog.databaseURL ||
						currentCatalog.isRepartitionInProgress !== prevCatalog.isRepartitionInProgress)
				) {
					this.reloadOnPartitionChange();
				}
			});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * To fetch the catalogProjects for a given project in firebase
	 * @param projectId projectId
	 * @param type ProjectCatalogType
	 * @returns Observables of catalogProjects data
	 */
	public getProjectCatalog$(projectId: string, type: ProjectCatalogType): Observable<IProjectCatalogData> {
		if (this.featureFlagManager.getFlag(DynamicFlagTypes.PROJECT_PARTITIONING_ENABLED)) {
			return this.catalogJar.get$(this.mergeIds(projectId, type));
		} else {
			return of(masterCatalog.catalog());
		}
	}

	/**
	 * To fetch the catalogProjects for a given project in firebase, this is the synchronous version
	 * Key caveat is this returns undefined for a project that has not been fetched yet, while getProjectCatalog$ fetches it and returns an observable
	 * @param projectId projectId
	 * @param type ProjectCatalogType
	 * @returns Observables of catalogProjects data
	 */
	public getProjectCatalogSync(projectId: string, type: ProjectCatalogType): IProjectCatalogData {
		if (this.featureFlagManager.getFlag(DynamicFlagTypes.PROJECT_PARTITIONING_ENABLED)) {
			return this.catalogJar.getSync(this.mergeIds(projectId, type));
		} else {
			return masterCatalog.catalog();
		}
	}

	/**
	 * Completes/removes any existing subscriptions in the cache if user no longer has access to the project
	 * @param userIndices user indices - projects current use has access to
	 */
	public cleanUpCache(userIndices: Map<string, UserIndex>): void {
		const keys = this.catalogJar.keys;
		for (const combinedKey of keys) {
			const key = this.unwrapIds(combinedKey).projectId;
			if (!userIndices.has(key)) {
				this.catalogJar.delete(key);
			}
		}
	}

	private mergeIds(projectId: string, type: ProjectCatalogType) {
		return [projectId, type].join('/');
	}
	private unwrapIds(combinedId: string) {
		const arr = combinedId.split('/');
		return { projectId: arr[0], type: arr[1] as ProjectCatalogType };
	}

	private processProjectCatalog$(combinedId: string): Observable<IProjectCatalogData> {
		const { projectId, type } = this.unwrapIds(combinedId);

		const path = fancyFirebaseGetters.getCatalogProjectsPath(projectId, type);
		return this.angularFireDatabase
			.object<IProjectCatalogData>(`${environment.refUrl}/${path}`)
			.valueChanges()
			.pipe(
				//some error detection
				catchError((err: FirebaseStateError) => {
					//every error from firebase is permission denied, decorate it like that to differentiate from ours
					err.message = PARTITIONING_ERROR_FIREBASE_PERMISSION;
					err.projectId = projectId;
					throw err;
				}),
				tap((data: IProjectCatalogData) => {
					if (!data) {
						throw new FirebaseStateError(PARTITIONING_ERROR_PROJECT_CATALOG_NOT_FOUND, projectId);
					}
					if (data.isRepartitionInProgress) {
						throw new FirebaseStateError(PARTITIONING_ERROR_REPARTITION_IN_PROGRESS, projectId);
					}
				}),
				catchError((err) => {
					this.reportErrorsToConsole(err);
					throw err;
				}),
				takeUntil(this.destroy$)
			);
	}

	//this is fine for now I guess? Not really interested in the stack
	private reportErrorsToConsole<T>(err: FirebaseStateError) {
		this.logger.log(err.message, err.projectId);
	}

	private reloadOnPartitionChange(): void {
		location.replace(''); //go back to summary page
	}
}
